import { Link } from 'react-router-dom';
import { isUnread, useStore } from '../store';
import { openModal } from '../store/ui';
import { Icon, cx } from './ui';

function GuildIcon({ guildId, active }: { guildId: string; active: boolean }) {
  const guild = useStore((s) => s.guilds[guildId]);
  const unread = useStore((s) =>
    Object.values(s.channels).some(
      (c) => c.guildId === guildId && c.type === 0 && isUnread(s, c.id),
    ),
  );
  const mentions = useStore((s) =>
    Object.values(s.channels)
      .filter((c) => c.guildId === guildId)
      .reduce((n, c) => n + (s.readStates[c.id]?.mentionCount ?? 0), 0),
  );
  const muted = useStore((s) => !!s.notificationSettings[guildId]?.muted);
  if (!guild) return null;
  const initials = guild.name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 3);
  return (
    <div className="group relative flex justify-center">
      <span
        className={cx(
          'absolute left-0 top-1/2 w-1 -translate-y-1/2 rounded-r bg-text-0 transition-all',
          active ? 'h-10' : unread && !muted ? 'h-2' : 'h-0 group-hover:h-5',
        )}
      />
      <Link
        to={`/channels/${guildId}`}
        title={guild.name}
        className={cx(
          'flex h-12 w-12 items-center justify-center overflow-hidden text-sm font-semibold transition-all',
          active
            ? 'rounded-2xl bg-accent text-white'
            : 'rounded-3xl bg-bg-2 text-text-1 hover:rounded-2xl hover:bg-accent hover:text-white',
        )}
      >
        {guild.iconUrl ? (
          <img src={guild.iconUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          initials
        )}
      </Link>
      {mentions > 0 && (
        <span className="absolute -bottom-0.5 right-2 rounded-full bg-danger px-1.5 text-xs font-bold text-white ring-4 ring-bg-0">
          {mentions}
        </span>
      )}
    </div>
  );
}

export function ServerRail({ activeGuildId }: { activeGuildId: string | null }) {
  const order = useStore((s) => s.guildOrder);
  const isAdmin = useStore((s) => s.me?.isAdmin);
  const dmUnread = useStore(
    (s) => Object.values(s.channels).filter((c) => c.guildId === null && isUnread(s, c.id)).length,
  );
  return (
    <nav
      className="flex w-[72px] shrink-0 flex-col items-center gap-2 overflow-y-auto bg-bg-0 py-3"
      aria-label="Servers"
    >
      <div className="relative">
        <Link
          to="/channels/@me"
          title="Direct messages"
          className={cx(
            'flex h-12 w-12 items-center justify-center transition-all',
            activeGuildId === '@me'
              ? 'rounded-2xl bg-accent text-white'
              : 'rounded-3xl bg-bg-2 text-text-1 hover:rounded-2xl hover:bg-accent hover:text-white',
          )}
        >
          <Icon name="message" className="h-6 w-6" />
        </Link>
        {dmUnread > 0 && (
          <span className="absolute -bottom-0.5 -right-1 rounded-full bg-danger px-1.5 text-xs font-bold text-white ring-4 ring-bg-0">
            {dmUnread}
          </span>
        )}
      </div>
      <div className="mx-auto h-0.5 w-8 rounded bg-bg-3" />
      {order.map((id) => (
        <GuildIcon key={id} guildId={id} active={activeGuildId === id} />
      ))}
      <button
        type="button"
        onClick={() => openModal({ kind: 'createGuild' })}
        title="Add a server"
        className="flex h-12 w-12 items-center justify-center rounded-3xl bg-bg-2 text-success transition-all hover:rounded-2xl hover:bg-success hover:text-white"
      >
        <Icon name="plus" className="h-6 w-6" />
      </button>
      {isAdmin && (
        <Link
          to="/admin"
          title="Instance admin"
          className="flex h-12 w-12 items-center justify-center rounded-3xl bg-bg-2 text-warning transition-all hover:rounded-2xl hover:bg-warning hover:text-black"
        >
          <Icon name="shield" className="h-6 w-6" />
        </Link>
      )}
    </nav>
  );
}
